import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Stack, TextField } from "@mui/material";
import { FilterModal } from "@/components/FilterModal";
import { LargeDialog } from "@/components/LargeDialog";
import { TransferList } from "@/components/TransferList";
import { useFilterState } from "@/hooks/useFilterState";
import type { User } from "@/api/user.service";
import { ESCALA_PAPEL_DESCRICAO, type ESCALA_ROLES } from "@/api/escala.service";

interface EscalaPermissoesModalProps {
  // Papel sendo vinculado — null fecha o modal. Um só de cada vez: quem
  // decide qual é o botão "Vincular pessoas" da linha daquele papel na
  // tabela, não uma aba dentro do modal.
  role: (typeof ESCALA_ROLES)[number] | null;
  users: User[];
  vinculadosIds: string[];
  // Papel atual de cada usuário (inclui outros papéis além de `role`) — só
  // pra avisar "atualmente Gerente" em quem aparece do lado Disponíveis,
  // já que a Escala aceita um papel só por usuário e mover alguém aqui
  // troca o que ele tinha.
  roleByUser: Map<string, string>;
  onClose: () => void;
  onSave: (ids: string[]) => Promise<void>;
}

// Twin column staged: as trocas só viram grant/revoke ao "Salvar" (mesmo
// padrão de EscalaVinculoModal) — Cancelar descarta tudo.
export function EscalaPermissoesModal({ role, users, vinculadosIds, roleByUser, onClose, onSave }: EscalaPermissoesModalProps) {
  const [working, setWorking] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const { inputValues, setInputValues, filtros, applyFilters, handleClearFilters } = useFilterState({ nome: "" });

  useEffect(() => {
    if (role) setWorking(vinculadosIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const filteredUsers = useMemo(() => {
    const q = filtros.nome.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
  }, [users, filtros]);

  const activeFilterCount = filtros.nome ? 1 : 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(working);
      onClose();
    } catch {
      // Erro já notificado por quem chama onSave — mantém o modal aberto
      // (e o que já vinculou fica staged) pra dar pra tentar de novo.
    } finally {
      setSaving(false);
    }
  };

  return (
    <LargeDialog open={!!role} title={`Vincular pessoas — ${role ?? ""}`} onClose={onClose} size="medium">
      {role && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {ESCALA_PAPEL_DESCRICAO[role].completo}
        </Alert>
      )}

      <FilterModal activeCount={activeFilterCount} onApply={applyFilters} onClear={handleClearFilters}>
        <TextField
          label="Nome ou email"
          size="small"
          fullWidth
          value={inputValues.nome}
          onChange={(e) => setInputValues({ nome: e.target.value })}
        />
      </FilterModal>

      <Box sx={{ flex: 1, minHeight: 0, mt: 2, overflow: "auto" }}>
        <TransferList<User>
          items={filteredUsers}
          selectedIds={working}
          onChange={setWorking}
          getId={(u) => u.id}
          getLabel={(u) => u.name}
          getSecondaryLabel={(u) => {
            const outroPapel = roleByUser.get(u.id);
            return outroPapel && outroPapel !== role ? `${u.email} — atualmente ${outroPapel}` : u.email;
          }}
          leftTitle="Disponíveis"
          rightTitle="Vinculados"
          itemNoun="pessoa(s)"
          hideSearch
        />
      </Box>

      <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          Salvar
        </Button>
      </Stack>
    </LargeDialog>
  );
}
